import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export function QuickDiscovery() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: "Quick Inquiry",
          email,
          company: "Unknown",
          message: "User requested quick discovery/pricing via home page widget.",
          service: "Discovery Call"
        }),
      });

      if (!res.ok) throw new Error("Submission failed");
      
      setIsSuccess(true);
      toast.success("We've received your request! Check your inbox soon.");
      setEmail("");
    } catch (error) {
      console.error("Discovery error:", error);
      toast.error("Something went wrong. Please try again or email us at hello@acceleratebyte.com.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background Accents */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto bg-card/50 backdrop-blur-xl border border-border/50 rounded-[2.5rem] p-8 md:p-16 shadow-2xl overflow-hidden relative group">
          {/* Decorative Border Gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold mb-6"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                FAST TRACK
              </motion.div>
              
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="font-display text-3xl md:text-4xl font-bold mb-4 leading-tight"
              >
                Ready to accelerate your <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-400">digital growth?</span>
              </motion.h2>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-muted-foreground text-lg mb-0"
              >
                Drop your work email below. We'll send our full service catalog and custom pricing in under 5 minutes.
              </motion.p>
            </div>

            <div className="relative">
              {isSuccess ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center p-8 bg-primary/10 rounded-2xl border border-primary/20 text-center"
                >
                  <CheckCircle2 className="h-12 w-12 text-primary mb-4" />
                  <h3 className="font-bold text-xl mb-2">Check your inbox!</h3>
                  <p className="text-sm text-muted-foreground">Our team has been notified. We're sending your discovery kit right now.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="relative group/input">
                    <Input
                      type="email"
                      placeholder="Enter your work email..."
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-16 pl-6 pr-32 bg-background/50 border-border/50 text-lg rounded-2xl focus:ring-primary/20 transition-all shadow-inner"
                      disabled={isSubmitting}
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      <Button 
                        type="submit" 
                        disabled={isSubmitting}
                        className="h-12 px-6 rounded-xl font-bold bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 group/btn"
                      >
                        {isSubmitting ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <>
                            Send <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground text-center px-4">
                    By submitting, you agree to receive a one-time project discovery kit. No spam, ever.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
